import React, { useState, useEffect } from "react";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import CustomButton from "components/common/Button";
import Text from "components/common/Text";
import TextCheckBox from "components/common/TextCheckBox";

import "./PermissionDialog.scss";

const USER_TYPE_OPTIONS = [
  { value: "edge", label: "Edge" },
  { value: "customer", label: "Customer" },
  { value: "employee", label: "Employee" },
];

/**
 * PermissionDialog — create or edit a permission that belongs to a feature.
 *
 * ID derivation:
 *   - Permission_ID = "perm_" + Key  (e.g. "perm_billing.override")
 *   - Action       = last segment of Key after the final "." (e.g. "override")
 *     When the admin fills in Action the Key is auto-composed as
 *     "<feature.key>.<action>" and Permission_ID follows from that.
 *
 * props: { open, mode: "create"|"edit", permission, feature, onClose, onSubmit }
 * feature: { featureId, key, name }
 *
 * onSubmit receives:
 *   { mode, permissionId, Permission_ID, Feature_ID, Key, Action, Name,
 *     Applicable_User_Types, Customer_Grantable }
 */
export default function PermissionDialog({ open, mode, permission, feature, onClose, onSubmit }) {
  const [action, setAction] = useState("");
  const [name, setName] = useState("");
  const [userTypes, setUserTypes] = useState([]);
  const [customerGrantable, setCustomerGrantable] = useState(false);

  // Derived key = "<feature.key>.<action>"  (shown read-only in create mode)
  const derivedKey = feature?.key && action.trim() ? `${feature.key}.${action.trim().toLowerCase()}` : "";

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && permission) {
      // Reverse-derive action from the permission key suffix
      const parts = (permission.key || "").split(".");
      setAction(parts.length > 1 ? parts[parts.length - 1] : permission.action || "");
      setName(permission.name || "");
      setUserTypes(permission.userTypes || []);
      setCustomerGrantable(!!permission.customerGrantable);
    } else {
      setAction("");
      setName("");
      setUserTypes([]);
      setCustomerGrantable(false);
    }
  }, [open, mode, permission]);

  const canSubmit = derivedKey !== "" && action.trim() !== "" && name.trim() !== "";

  const toggleUserType = (value) => {
    setUserTypes((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const submit = () => {
    const trimmedAction = action.trim().toLowerCase();
    const key = derivedKey || `${feature?.key || ""}.${trimmedAction}`;
    onSubmit({
      mode,
      permissionId: permission?.permissionId,
      // Client-side ID derivation: "perm_" + full key
      Permission_ID: `perm_${key}`,
      Feature_ID: feature?.featureId,
      Key: key,
      Action: trimmedAction,
      Name: name.trim(),
      Applicable_User_Types: userTypes,
      Customer_Grantable: customerGrantable,
    });
  };

  const featureLabel = feature ? `${feature.name} (${feature.key})` : "";

  const dialogContent = (
    <DialogContent>
      <div className="permission-dialog-fields">
        <Text
          name="perm-action"
          label="Action (e.g. override)"
          value={action}
          disabled={mode === "edit"}
          onChange={(e) => setAction(e.target.value.toLowerCase().replace(/\s+/g, "_"))}
          helperText={
            mode === "create"
              ? `Key will be "${feature?.key || "<feature>"}.${action || "<action>"}". Permission_ID = "perm_" + key.`
              : "Action is immutable after creation."
          }
        />
        {mode === "create" && derivedKey && (
          <Text name="perm-derived-key" label="Derived key (read-only)" value={derivedKey} disabled />
        )}
        <Text name="perm-name" label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <div>
          <p className="permission-dialog__user-types-label para-body-m-medium">Applicable user types</p>
          <div className="permission-dialog__user-types">
            {USER_TYPE_OPTIONS.map((opt) => (
              <TextCheckBox
                key={opt.value}
                text={opt.label}
                checked={userTypes.includes(opt.value)}
                onChange={() => toggleUserType(opt.value)}
              />
            ))}
          </div>
        </div>
        <TextCheckBox
          text="Customer grantable"
          checked={customerGrantable}
          onChange={(e) => setCustomerGrantable(e?.target?.checked ?? !customerGrantable)}
        />
      </div>
    </DialogContent>
  );

  const dialogActions = (
    <DialogActions
      action1={<CustomButton label="Cancel" variant="text" onClick={onClose} />}
      action2={<CustomButton label="Save" variant="contained" onClick={submit} isDisabled={!canSubmit} />}
    />
  );

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      size="sm"
      title={
        <DialogTitle
          title={mode === "edit" ? `Edit permission "${permission?.name || ""}"` : `Add permission to "${featureLabel}"`}
        />
      }
      content={dialogContent}
      actions={dialogActions}
    />
  );
}
